import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from "recharts";
import { Typography, Box, Divider } from "@mui/material";
import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useChartPalette } from "../utils/chartUtils";
import { getCartesianGridProps } from "../utils/chartStyles";
import { useAppTheme } from "@/hooks/useAppTheme";
import type { EnrollmentBucket } from "@/types";

/**
 * Stacked bar chart showing total enrolled members grouped by specific deductible buckets of $50k,
 * with each bar broken down by carrier.
 *
 * @param data - Pre-aggregated enrollment buckets from the analytics endpoint.
 */
export const SpecDeductibleEnrollmentBarChart = ({ data: rawData }: { data: EnrollmentBucket[] }) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  if (rawData.length === 0) {
    return <EmptyChartState />;
  }

  // Calculate carrier totals for sorting (largest at the base of the stack)
  const carrierTotals: Record<string, number> = {};
  rawData.forEach((row) => {
    carrierTotals[row.carrier_name] = (carrierTotals[row.carrier_name] ?? 0) + row.enrollment_total;
  });

  const allCarriers = Object.entries(carrierTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([carrier]) => carrier);

  // Pivot flat rows into Recharts format: { name, [carrier1]: count, [carrier2]: count, ... }
  const bucketMap: Record<number, Record<string, number>> = {};
  rawData.forEach((row) => {
    if (!bucketMap[row.bucket_floor]) {
      bucketMap[row.bucket_floor] = {};
    }
    bucketMap[row.bucket_floor][row.carrier_name] = row.enrollment_total;
  });

  const data = Object.entries(bucketMap)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([bucket, carriers]) => {
      const bucketNum = Number(bucket);
      const bucketEnd = bucketNum + 50000;
      return {
        name: `$${bucketNum / 1000}k-${bucketEnd / 1000}k`,
        bucket: bucketNum,
        ...allCarriers.reduce(
          (acc, carrier) => {
            acc[carrier] = carriers[carrier] || 0;
            return acc;
          },
          {} as Record<string, number>
        ),
      };
    });

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <Tooltip content={<CustomTooltip />} />
        {allCarriers.map((carrier, index) => (
          <Bar key={carrier} dataKey={carrier} stackId="enrollment" fill={chartPalette[index % chartPalette.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// Custom tooltip for the stacked enrollment bar chart
const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || !payload.length) return null;

  const bucketName = payload[0].payload.name;

  // Calculate total enrollment across all carriers
  const total = payload.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {bucketName}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {payload
        .filter((entry) => Number(entry.value) > 0)
        .reverse()
        .map((entry, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="body2">{entry.name}:</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {Number(entry.value).toLocaleString()}
            </Typography>
          </Box>
        ))}
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Total:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {total.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};
